import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Star, Download, Filter, Grid, List, ExternalLink, Clock, User, Tag } from 'lucide-react';
import { PageContainer } from '../../components/ui/PageContainer';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { api } from '../../lib/api';

interface MarketplaceTemplate {
  id: string;
  name: string;
  type: string;
  category: string;
  version: string;
  description?: string;
  rating: number | null;
  downloads: number;
  createdBy: string;
  createdAt: string;
  isPublic: boolean;
}

interface TemplateStats {
  totalInstances: number;
  activeInstances: number;
  averageRating: number;
  totalRatings: number;
  recentDownloads: number;
}

interface TemplateVersion {
  id: string;
  version: string;
  changelog: string | null;
  createdBy: string;
  createdAt: string;
}

export function Marketplace() {
  const queryClient = useQueryClient();
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<MarketplaceTemplate | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Fetch marketplace templates
  const { data: templates, isLoading } = useQuery({
    queryKey: ['marketplace-templates', searchQuery, categoryFilter, typeFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (categoryFilter) params.append('category', categoryFilter);
      if (typeFilter) params.append('type', typeFilter);
      const response = await api.get(`/provider-templates/marketplace?${params}`);
      return response.data as MarketplaceTemplate[];
    },
  });

  // Fetch template stats
  const { data: templateStats } = useQuery({
    queryKey: ['template-stats', selectedTemplate?.id],
    queryFn: async () => {
      if (!selectedTemplate) return null;
      const response = await api.get(`/provider-templates/marketplace/${selectedTemplate.id}/stats`);
      return response.data as TemplateStats;
    },
    enabled: !!selectedTemplate,
  });

  // Fetch template versions
  const { data: templateVersions } = useQuery({
    queryKey: ['template-versions', selectedTemplate?.id],
    queryFn: async () => {
      if (!selectedTemplate) return [];
      const response = await api.get(`/provider-templates/${selectedTemplate.id}/versions`);
      return response.data as TemplateVersion[];
    },
    enabled: !!selectedTemplate,
  });

  // Create instance from template
  const createInstanceMutation = useMutation({
    mutationFn: async (templateId: string) => {
      const response = await api.post('/provider-instances', { templateId });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['provider-instances'] });
      setShowDetailsModal(false);
    },
  });

  const handleViewDetails = (template: MarketplaceTemplate) => {
    setSelectedTemplate(template);
    setShowDetailsModal(true);
  };

  const getCategoryBadgeVariant = (category: string) => {
    switch (category) {
      case 'BAAS': return 'primary';
      case 'CRYPTO': return 'warning';
      case 'CARDS': return 'info';
      case 'BENEFITS': return 'success';
      default: return 'default';
    }
  };

  const getTypeBadgeVariant = (type: string) => {
    switch (type) {
      case 'REST_API': return 'primary';
      case 'GRAPHQL': return 'info';
      case 'BLOCKCHAIN_EVM': return 'warning';
      case 'BLOCKCHAIN_SOLANA': return 'success';
      case 'SOAP': return 'default';
      case 'GRPC': return 'default';
      default: return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const renderStars = (rating: number | null) => {
    const stars = rating || 0;
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={14}
            className={star <= stars ? 'text-yellow-400 fill-yellow-400' : 'text-gray-400'}
          />
        ))}
        {rating && <span className="text-sm text-gray-400 ml-1">({rating.toFixed(1)})</span>}
      </div>
    );
  };

  return (
    <PageContainer>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Marketplace de Templates</h1>
        <p className="text-gray-400">Explore e utilize templates públicos de providers</p>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <div className="flex flex-wrap gap-4 items-center">
          <div className="flex-1 min-w-[200px]">
            <Input
              placeholder="Buscar templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              leftIcon={<Search size={18} />}
            />
          </div>
          
          <Select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="w-40"
          >
            <option value="">Todas Categorias</option>
            <option value="BAAS">BAAS</option>
            <option value="CRYPTO">Crypto</option>
            <option value="CARDS">Cards</option>
            <option value="BENEFITS">Benefits</option>
            <option value="ERP">ERP</option>
            <option value="ECOMMERCE">E-commerce</option>
          </Select>

          <Select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="w-40"
          >
            <option value="">Todos Tipos</option>
            <option value="REST_API">REST API</option>
            <option value="GRAPHQL">GraphQL</option>
            <option value="BLOCKCHAIN_EVM">EVM</option>
            <option value="BLOCKCHAIN_SOLANA">Solana</option>
            <option value="SOAP">SOAP</option>
            <option value="GRPC">gRPC</option>
          </Select>

          <div className="flex gap-2">
            <Button
              variant={viewMode === 'grid' ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('grid')}
            >
              <Grid size={18} />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'primary' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
            >
              <List size={18} />
            </Button>
          </div>
        </div>
      </Card>

      {/* Templates Grid/List */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
        </div>
      ) : templates && templates.length > 0 ? (
        <div className={viewMode === 'grid' 
          ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' 
          : 'flex flex-col gap-4'
        }>
          {templates.map((template) => (
            <Card 
              key={template.id} 
              className="hover:border-primary-500/50 transition-colors cursor-pointer"
              onClick={() => handleViewDetails(template)}
            >
              <div className="flex flex-col h-full">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-semibold text-lg">{template.name}</h3>
                    <p className="text-sm text-gray-400">v{template.version}</p>
                  </div>
                  <Badge variant={getCategoryBadgeVariant(template.category)}>
                    {template.category}
                  </Badge>
                </div>

                <div className="flex gap-2 mb-3">
                  <Badge variant={getTypeBadgeVariant(template.type)} size="sm">
                    {template.type.replace('_', ' ')}
                  </Badge>
                </div>

                <div className="flex-1">
                  <p className="text-sm text-gray-400 line-clamp-2">
                    {template.description || 'Sem descrição disponível'}
                  </p>
                </div>

                <div className="mt-4 pt-4 border-t border-surface-700">
                  <div className="flex items-center justify-between">
                    {renderStars(template.rating)}
                    <div className="flex items-center gap-1 text-sm text-gray-400">
                      <Download size={14} />
                      <span>{template.downloads}</span>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="text-center py-12">
          <Filter size={48} className="mx-auto text-gray-500 mb-4" />
          <h3 className="text-lg font-medium mb-2">Nenhum template encontrado</h3>
          <p className="text-gray-400">
            Tente ajustar os filtros ou buscar por outro termo
          </p>
        </Card>
      )}

      {/* Template Details Modal */}
      <Modal
        isOpen={showDetailsModal}
        onClose={() => setShowDetailsModal(false)}
        title={selectedTemplate?.name || 'Detalhes do Template'}
        size="lg"
      >
        {selectedTemplate && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <Badge variant={getCategoryBadgeVariant(selectedTemplate.category)}>
                    {selectedTemplate.category}
                  </Badge>
                  <Badge variant={getTypeBadgeVariant(selectedTemplate.type)}>
                    {selectedTemplate.type.replace('_', ' ')}
                  </Badge>
                </div>
                <p className="text-gray-400">
                  {selectedTemplate.description || 'Sem descrição disponível'}
                </p>
              </div>
            </div>

            {/* Stats */}
            {templateStats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="text-center p-4">
                  <div className="text-2xl font-bold text-primary-400">
                    {templateStats.totalInstances}
                  </div>
                  <div className="text-sm text-gray-400">Instâncias</div>
                </Card>
                <Card className="text-center p-4">
                  <div className="text-2xl font-bold text-green-400">
                    {templateStats.activeInstances}
                  </div>
                  <div className="text-sm text-gray-400">Ativas</div>
                </Card>
                <Card className="text-center p-4">
                  <div className="text-2xl font-bold text-yellow-400">
                    {templateStats.averageRating?.toFixed(1) || '-'}
                  </div>
                  <div className="text-sm text-gray-400">Avaliação</div>
                </Card>
                <Card className="text-center p-4">
                  <div className="text-2xl font-bold text-blue-400">
                    {templateStats.recentDownloads}
                  </div>
                  <div className="text-sm text-gray-400">Downloads (30d)</div>
                </Card>
              </div>
            )}

            {/* Info */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2 text-gray-400">
                <Tag size={16} />
                <span>Versão: {selectedTemplate.version}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-400">
                <User size={16} />
                <span>Criado por: {selectedTemplate.createdBy}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-400">
                <Clock size={16} />
                <span>Criado em: {formatDate(selectedTemplate.createdAt)}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-400">
                <Download size={16} />
                <span>{selectedTemplate.downloads} downloads</span>
              </div>
            </div>

            {/* Version History */}
            {templateVersions && templateVersions.length > 0 && (
              <div>
                <h4 className="font-medium mb-3">Histórico de Versões</h4>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {templateVersions.map((version) => (
                    <div 
                      key={version.id}
                      className="flex items-center justify-between p-3 bg-surface-800 rounded-lg"
                    >
                      <div>
                        <span className="font-medium">v{version.version}</span>
                        {version.changelog && (
                          <p className="text-sm text-gray-400 mt-1">{version.changelog}</p>
                        )}
                      </div>
                      <span className="text-sm text-gray-500">
                        {formatDate(version.createdAt)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-surface-700">
              <Button variant="ghost" onClick={() => setShowDetailsModal(false)}>
                Fechar
              </Button>
              <Button
                variant="primary"
                onClick={() => createInstanceMutation.mutate(selectedTemplate.id)}
                isLoading={createInstanceMutation.isPending}
              >
                <ExternalLink size={18} className="mr-2" />
                Criar Instância
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </PageContainer>
  );
}

export default Marketplace;
